import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button, Card, Chip, EmptyState, Skeleton, providerTag } from '../ui'
import { api } from '../../api/client'
import { useMergeVerification } from '../../api/hooks'
import type { MergeDoor, SystemProvider } from '../../api/types'
import { useAuthStore } from '../../stores/auth'

/** How long a retry re-opens polling for a settled-but-failed verification. */
const RETRY_WINDOW_MS = 60_000

function stateTone(state: string): 'ok' | 'warn' | 'danger' | 'muted' {
  return state === 'ok' ? 'ok' : state === 'pending' ? 'warn' : state === 'error' ? 'danger' : 'muted'
}

function doorLabel(doors: MergeDoor[], uid: string): string {
  return doors.find((d) => d.door === uid)?.name ?? uid
}

/**
 * Post-merge verification: are the survivor's effective doors what the
 * operator approved, and has every vendor system (and every card on it)
 * caught up? Polls until the sync settles; a failed system offers a retry
 * (POST /systems/{uid}/push) that re-opens polling.
 */
export function MergeVerificationCard({
  survivor,
  verification,
  pollMs = 3000,
}: {
  survivor: string
  verification: string
  pollMs?: number
}) {
  const [pollUntil, setPollUntil] = useState(0)
  const canOperate = useAuthStore((s) => s.customer?.role === 'admin' || s.customer?.role === 'operator')
  const check = useMergeVerification(survivor, verification, pollMs, pollUntil)
  const retry = useMutation({
    mutationFn: (systemUid: string) => api.pushSystemNow(systemUid),
    onSuccess: () => {
      setPollUntil(Date.now() + RETRY_WINDOW_MS)
      void check.refetch()
    },
  })

  if (check.isPending) {
    return (
      <Card title="Verification">
        <Skeleton className="h-24" />
      </Card>
    )
  }
  if (check.isError) {
    return (
      <Card title="Verification">
        <EmptyState
          title="Could not load the verification"
          hint={check.error.message}
          action={
            <Button variant="ghost" size="sm" onClick={() => void check.refetch()}>
              Try again
            </Button>
          }
        />
      </Card>
    )
  }

  const v = check.data
  const allDoors = [...v.approved_doors, ...v.effective_doors]
  const match = v.ostrel_access_match
  const overall = !v.settled ? (
    <Chip tone="warn" label="Syncing…" />
  ) : v.ok ? (
    <Chip tone="ok" label="Verified" />
  ) : (
    <Chip tone="danger" label="Needs attention" />
  )

  return (
    <Card
      title="Verification"
      action={
        <span className="flex items-center gap-2">
          {overall}
          <Button variant="ghost" size="sm" disabled={check.isFetching} onClick={() => void check.refetch()}>
            {check.isFetching ? 'Checking…' : 'Check again'}
          </Button>
        </span>
      }
    >
      <div className="flex flex-col gap-4" data-testid="merge-verification">
        {!v.settled && (
          <p className="text-xs text-ostrel-ink-muted">
            Waiting for the vendor systems to pick up the change — this page checks again automatically.
          </p>
        )}

        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            {match ? 'Ostrel access matches' : 'Ostrel access differs'}
          </p>
          {match ? (
            <p className="text-xs text-ostrel-ok">
              In Ostrel, the merged person opens exactly the {v.approved_doors.length} approved door
              {v.approved_doors.length === 1 ? '' : 's'}. The vendor systems confirm separately below.
            </p>
          ) : (
            <ul className="flex flex-col gap-1 text-xs text-ostrel-danger">
              {v.missing.map((d) => (
                <li key={`m-${d}`}>Missing: {doorLabel(allDoors, d)}</li>
              ))}
              {v.extra.map((d) => (
                <li key={`e-${d}`}>Not approved: {doorLabel(allDoors, d)}</li>
              ))}
              {v.schedule_mismatch.map((d) => (
                <li key={`s-${d}`}>Different schedule: {doorLabel(allDoors, d)}</li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Credentials
          </p>
          {v.credentials.length === 0 ? (
            <p className="text-xs text-ostrel-ink-muted">The merged person has no credentials.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-ostrel-line">
              {v.credentials.map((c) => (
                <li key={c.unique_id} className="flex flex-wrap items-center gap-2 py-2" data-testid="verify-credential">
                  <Chip tone="muted" label={c.kind} />
                  <span className="tnum font-mono text-sm text-ostrel-ink">{c.code}</span>
                  <Chip tone={c.status === 'active' ? 'ok' : 'muted'} label={c.status} />
                  <Chip
                    tone={c.ostrel_access_match ? 'ok' : 'danger'}
                    label={c.ostrel_access_match ? 'Ostrel access matches' : 'Ostrel access differs'}
                  />
                  <span className="ml-auto flex flex-wrap items-center gap-1.5">
                    {c.systems.length === 0 ? (
                      <span className="text-[11px] text-ostrel-ink-muted">not on any vendor system</span>
                    ) : (
                      c.systems.map((s) => (
                        <Chip
                          key={s.system}
                          tone={stateTone(s.state)}
                          label={`${providerTag(s.provider as SystemProvider)} ${s.state}`}
                          title={s.last_error || undefined}
                        />
                      ))
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {v.systems.length > 0 && (
          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Vendor systems
            </p>
            <ul className="flex flex-col divide-y divide-ostrel-line">
              {v.systems.map((s) => (
                <li key={s.uid} className="flex flex-wrap items-center gap-2 py-2" data-testid="verify-system">
                  <span className="text-sm text-ostrel-ink">{s.name}</span>
                  <Chip tone="muted" label={providerTag(s.provider as SystemProvider)} />
                  <Chip tone={stateTone(s.state)} label={s.state} />
                  {s.dry_run && <Chip tone="muted" label="dry run" title="Changes never reach the vendor" />}
                  {!s.enabled && <Chip tone="muted" label="disabled" />}
                  {s.pending > 0 && <span className="tnum text-[11px] text-ostrel-ink-muted">{s.pending} pending</span>}
                  {s.last_error && <span className="w-full text-[11px] text-ostrel-danger">{s.last_error}</span>}
                  {s.state === 'error' && (
                    <Button
                      className="ml-auto"
                      variant="ghost"
                      size="sm"
                      disabled={!canOperate || retry.isPending}
                      title={canOperate ? undefined : 'Only operators can push to a system'}
                      onClick={() => retry.mutate(s.uid)}
                    >
                      {retry.isPending && retry.variables === s.uid ? 'Retrying…' : 'Retry sync'}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {retry.isError && <p className="text-xs text-ostrel-danger">{retry.error.message}</p>}
      </div>
    </Card>
  )
}
